// apps/widget/src/lib/camera.ts
//
// getUserMedia, and the failure modes that actually happen.
//
// Every error here maps to a specific message and a specific next step. "Camera
// error" is the single fastest way to lose a scan — the visitor has no idea
// whether to retry, grant a permission, or use their phone.

import { IMAGE } from '@skinvizo/shared';

export type CameraFailure =
  | 'DENIED'
  | 'NO_DEVICE'
  | 'IN_USE'
  | 'INSECURE_CONTEXT'
  | 'UNSUPPORTED'
  | 'UNKNOWN';

export interface CameraError {
  failure: CameraFailure;
  message: string;
  /** True when handing off to a phone is the right next step. */
  suggestHandoff: boolean;
}

const MESSAGES: Record<CameraFailure, { message: string; suggestHandoff: boolean }> = {
  DENIED: {
    message: 'Camera access was blocked. Allow it in your browser settings, or use your phone instead.',
    suggestHandoff: true,
  },
  NO_DEVICE: {
    message: "We couldn't find a camera on this device.",
    suggestHandoff: true,
  },
  IN_USE: {
    message: 'Your camera is being used by another app. Close it and try again.',
    suggestHandoff: true,
  },
  INSECURE_CONTEXT: {
    message: 'This page needs a secure (HTTPS) connection to use the camera.',
    suggestHandoff: true,
  },
  UNSUPPORTED: {
    message: "This browser doesn't support camera capture.",
    suggestHandoff: true,
  },
  UNKNOWN: {
    message: "We couldn't start the camera.",
    suggestHandoff: true,
  },
};

export function describeFailure(failure: CameraFailure): CameraError {
  return { failure, ...MESSAGES[failure] };
}

export async function hasCamera(): Promise<boolean> {
  if (!navigator.mediaDevices?.enumerateDevices) return false;
  try {
    const devices = await navigator.mediaDevices.enumerateDevices();
    return devices.some((d) => d.kind === 'videoinput');
  } catch {
    return false;
  }
}

export async function startCamera(facingMode: 'user' | 'environment' = 'user'): Promise<MediaStream> {
  if (!window.isSecureContext && location.hostname !== 'localhost') {
    throw describeFailure('INSECURE_CONTEXT');
  }
  if (!navigator.mediaDevices?.getUserMedia) {
    throw describeFailure('UNSUPPORTED');
  }

  try {
    return await navigator.mediaDevices.getUserMedia({
      video: {
        // Ideal, not exact: `exact` fails outright on a laptop that cannot do
        // 720p, and a slightly smaller frame is far better than no scan.
        width: { ideal: IMAGE.MIN_CAPTURE_WIDTH },
        height: { ideal: IMAGE.MIN_CAPTURE_HEIGHT },
        facingMode,
        frameRate: { ideal: 30 },
      },
      audio: false,
    });
  } catch (e) {
    throw describeFailure(classify(e));
  }
}

function classify(error: unknown): CameraFailure {
  const name = (error as { name?: string })?.name;
  switch (name) {
    case 'NotAllowedError':
    case 'SecurityError':
      return 'DENIED';
    case 'NotFoundError':
    case 'OverconstrainedError':
      return 'NO_DEVICE';
    case 'NotReadableError':
    case 'AbortError':
      return 'IN_USE';
    default:
      return 'UNKNOWN';
  }
}

export function stopCamera(stream: MediaStream | null): void {
  // Every track, not just video. A stream left running keeps the browser's
  // camera indicator lit, which people find alarming and rightly so.
  stream?.getTracks().forEach((track) => track.stop());
}

/** Draws the current frame to a canvas at native resolution. */
export function captureFrame(video: HTMLVideoElement): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = video.videoWidth;
  canvas.height = video.videoHeight;

  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas is unavailable');

  // Front cameras preview mirrored because that is what people expect of a
  // mirror. The captured file must NOT be mirrored — a mole on the left cheek
  // has to stay on the left cheek for the report overlay to be right.
  ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
  return canvas;
}

export function canvasToBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('Could not encode the photo'))),
      'image/jpeg',
      IMAGE.JPEG_QUALITY,
    );
  });
}
